import { useAWS } from "hook/useAWS";

export interface HelpGuide {
  id: string;
  title: string;
  description?: string;
  groupName?: string;
  groupId: string;
  resourceUrl: string;
  resourceType: string;
  position: number;
  thumbnailUrl?: string;
  fileName?: string;
}

export interface HelpGuideGroup {
  groupName: string;
  groupId: string;
  sortOrder: number;
  layout: string;
  id?: string;
}

const itemsDocTableName = process.env.REACT_APP_ITEM_TABLE_NAME || "";
const groupsDocTableName = process.env.REACT_APP_GROUPS_TABLE_NAME || ""; // TODO: -> 'Settings'
const groupsDocId = process.env.REACT_APP_GROUPS_DOC_ID || "";

const changeSortOrderGroup = (groups: HelpGuideGroup[], orderId: string) => {
  const changeItem = groups.find((group) => group.groupId === orderId);
  if (!changeItem) return groups;
  groups.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
  const index = groups.findIndex((group) => group.groupId === orderId);
  groups.splice(index, 1);
  groups.splice(Number(changeItem.sortOrder) - 1, 0, changeItem);
  groups.forEach((group, index) => {
    group.sortOrder = index + 1;
  });
  return groups;
};

const changeSortOrderItem = (items: HelpGuide[], orderId?: string) => {
  if (orderId) {
    const changeItem = items.find((item) => item.id === orderId);
    if (!changeItem) return items;
    items.sort((a, b) => Number(a.position) - Number(b.position));
    const index = items.findIndex((item) => item.id === orderId);
    items.splice(index, 1);
    items.splice(Number(changeItem?.position) - 1, 0, changeItem);
  }
  items.forEach((item, index) => {
    item.position = index + 1;
  });
  return items;
};

const useHelpGuideApi = () => {
  const {
    docScan,
    docGet,
    docPut,
    docUpdate,
    docDelete,
    s3UploadFile,
    s3DeleteFile,
  } = useAWS();

  const getHelpGuideGroups = async (): Promise<HelpGuideGroup[]> => {
    if (!groupsDocTableName || !groupsDocId) {
      return [];
    }
    return docGet(groupsDocTableName, { id: groupsDocId }).then((res) => {
      return (res?.items || []).sort(
        (a: HelpGuideGroup, b: HelpGuideGroup) => a.sortOrder - b.sortOrder
      );
    });
  };

  const getHelpGuides = async (): Promise<HelpGuide[]> => {
    if (!itemsDocTableName) {
      return [];
    }
    return docScan(itemsDocTableName).then((res) => {
      const filteredItems = (res.Items || [])
        .filter((item: any) => {
          return item.resourceType && item.groupId;
        })
        .sort((a: HelpGuide, b: HelpGuide) => a.position - b.position);
      return filteredItems;
    });
  };

  const updateHelpGuideItem = async (item: HelpGuide, file?: File) => {
    const helpItems = await getHelpGuides();
    let changeGroup = false;
    let changedGroup = helpItems.filter(
      (helpItem) => helpItem.groupId === item.groupId
    );
    let changeItem =
      changedGroup[changedGroup.findIndex((cItem) => cItem.id === item.id)];
    if (!changeItem) {
      changeGroup = true;
      changeItem =
        helpItems[helpItems.findIndex((cItem) => cItem.id === item.id)];
    }
    if (file) {
      if (changeItem.resourceUrl) await s3DeleteFile(item.resourceUrl);
      const key = `${new Date().getTime()}${file.name}`;
      return s3UploadFile(file, key)
        .then((objectUrl) => objectUrl)
        .then((objectUrl) => {
          item.resourceUrl = objectUrl;
          item.fileName = file.name;
          changedGroup[
            changedGroup.findIndex((cItem) => cItem.id === item.id)
          ] = item;
          changedGroup = changeSortOrderItem(changedGroup, item.id);

          //현재 그룹만 재정렬 저장
          return Promise.all(
            changedGroup.map((item) => docPut(itemsDocTableName, item))
          );
        });
    }
    if (changeGroup) {
      changedGroup = helpItems.filter(
        (cItem) => cItem.groupId === item.groupId && cItem.id !== item.id
      );
      changedGroup.push(item);
    } else {
      changedGroup[changedGroup.findIndex((cItem) => cItem.id === item.id)] =
        item;
    }
    changedGroup = changeSortOrderItem(changedGroup, item.id);
    //현재 그룹만 재정렬 저장
    return Promise.all(
      changedGroup.map((item) => docPut(itemsDocTableName, item))
    );
  };

  const addHelpGuideItem = async (item: HelpGuide, file?: File) => {
    const helpItems = await getHelpGuides();
    let changedGroup = helpItems.filter(
      (helpItem) => helpItem.groupId === item.groupId
    );
    if (file) {
      const key = `${new Date().getTime()}${file.name}`;
      item.resourceUrl = await s3UploadFile(file, key).then(
        (objectUrl) => objectUrl
      );
      item.fileName = file.name;
    }
    if (item.position < changedGroup.length + 1) {
      changedGroup.push(item);
      changedGroup = changeSortOrderItem(changedGroup, item.id);
    } else {
      changedGroup.push(item);
    }
    //현재 그룹만 재정렬 저장
    return Promise.all(
      changedGroup.map((item) => docPut(itemsDocTableName, item))
    );
  };

  const deleteHelpGuideItem = async (item: HelpGuide) => {
    if (item.resourceType === "youtube") {
      await docDelete(itemsDocTableName, item.id);
    } else {
      await docDelete(itemsDocTableName, item.id).then(() => {
        return s3DeleteFile(item.resourceUrl);
      });
    }
    // 삭제된 아이템의 그룹에 속한 아이템들을 가져오고 정렬
    const helpItems = await getHelpGuides().then((res) =>
      res.filter((helpItem) => helpItem.groupId === item.groupId)
    );
    const changedItems = changeSortOrderItem(helpItems);
    return Promise.all(
      changedItems.map((item) => docPut(itemsDocTableName, item))
    );
  };

  const addHelpGuideGroup = (groupToAdd: HelpGuideGroup): Promise<any> => {
    groupToAdd.groupId = `${new Date().valueOf()}`;
    groupToAdd.id = groupToAdd.groupId;
    // Ensure sortOrder is a number
    const newGroup = {
      ...groupToAdd,
      sortOrder: Number(groupToAdd.sortOrder),
      groupId: groupToAdd.groupId,
    };
    return getHelpGuideGroups().then((groups) => {
      const items =
        groups.length > 0
          ? changeSortOrderGroup([...groups, newGroup], newGroup.groupId)
          : [newGroup];
      return docUpdate(groupsDocTableName, groupsDocId, { items });
    });
  };

  const updateHelpGuideGroup = async (
    groupToUpdate: HelpGuideGroup
  ): Promise<any> => {
    // Ensure sortOrder is a number
    const updatedGroup = {
      ...groupToUpdate,
      sortOrder: Number(groupToUpdate.sortOrder),
    };

    return getHelpGuideGroups().then((groups) => {
      let newGroups = [...groups];
      const index = groups.findIndex(
        (group) => group.groupId === updatedGroup.groupId
      );
      newGroups[index] = updatedGroup;
      if (updatedGroup.sortOrder !== groups[index].sortOrder) {
        newGroups = changeSortOrderGroup(newGroups, updatedGroup.groupId);
      }
      return docUpdate(groupsDocTableName, groupsDocId, { items: newGroups });
    });
  };

  const deleteHelpGuideGroup = async (
    groupToDelete: HelpGuideGroup
  ): Promise<any> => {
    return getHelpGuideGroups().then((groups) => {
      const index = groups.findIndex(
        (group) => group.groupId === groupToDelete.groupId
      );
      groups.splice(index, 1);
      return docUpdate(groupsDocTableName, groupsDocId, { items: groups });
    });
  };

  return {
    getHelpGuideGroups,
    getHelpGuides,
    addHelpGuideItem,
    deleteHelpGuideItem,
    addHelpGuideGroup,
    updateHelpGuideGroup,
    deleteHelpGuideGroup,
    updateHelpGuideItem,
  };
};

export default useHelpGuideApi;
